import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { Button } from "@/clientWeb/components/ui/button"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import {
  Zap, Diamond, Crown, Cloud, Presentation, Sparkles,
  RefreshCw, PlusCircle, ChevronRight,
  Star, Package, Puzzle
} from "lucide-react"
import { AddHostDialog } from "./AddHostDialog"
import "./LicensesManagement.css"

interface ActiveLicense {
  id: string
  licenseType: string
  planType: "standard" | "pro" | "business" | "free"
  participants: number
  renewalDate: string
  billingCycle: string
  isSelected?: boolean
  showManage?: boolean
}

const PLAN_CONFIG: Record<string, {
  label: string
  icon: React.ReactNode
  gradient: string
  glowColor: string
  badgeCls: string
  textCls: string
  accentCls: string
  iconColor: string
  iconBg: string
  btnCls: string
}> = {
  standard: {
    label: "Standard",
    icon: <Crown className="w-5 h-5" />,
    gradient: "from-amber-200 via-orange-200 to-amber-300",
    glowColor: "shadow-amber-100 dark:shadow-amber-900/20",
    badgeCls: "bg-amber-100 text-amber-600 dark:bg-amber-900/20 dark:text-amber-300",
    textCls: "text-amber-500 dark:text-amber-300",
    accentCls: "border-amber-200 dark:border-amber-700/30",
    iconColor: "text-amber-400",
    iconBg: "bg-amber-50 dark:bg-amber-900/20",
    btnCls: "bg-amber-400 text-white hover:bg-amber-500",
  },
  pro: {
    label: "Pro",
    icon: <Zap className="w-5 h-5" />,
    gradient: "from-blue-200 via-blue-300 to-indigo-300",
    glowColor: "shadow-blue-100 dark:shadow-blue-900/20",
    badgeCls: "bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-300",
    textCls: "text-blue-500 dark:text-blue-300",
    accentCls: "border-blue-200 dark:border-blue-700/30",
    iconColor: "text-blue-400",
    iconBg: "bg-blue-50 dark:bg-blue-900/20",
    btnCls: "bg-blue-400 text-white hover:bg-blue-500",
  },
  business: {
    label: "Business",
    icon: <Diamond className="w-5 h-5" />,
    gradient: "from-purple-200 via-violet-200 to-purple-300",
    glowColor: "shadow-purple-100 dark:shadow-purple-900/20",
    badgeCls: "bg-purple-100 text-purple-600 dark:bg-purple-900/20 dark:text-purple-300",
    textCls: "text-purple-500 dark:text-purple-300",
    accentCls: "border-purple-200 dark:border-purple-700/30",
    iconColor: "text-purple-400",
    iconBg: "bg-purple-50 dark:bg-purple-900/20",
    btnCls: "bg-purple-400 text-white hover:bg-purple-500",
  },
  free: {
    label: "Free",
    icon: <Star className="w-5 h-5" />,
    gradient: "from-slate-200 via-slate-300 to-slate-400",
    glowColor: "shadow-slate-100 dark:shadow-slate-800/20",
    badgeCls: "bg-slate-100 text-slate-500 dark:bg-slate-800/30 dark:text-slate-300",
    textCls: "text-slate-400 dark:text-slate-300",
    accentCls: "border-slate-200 dark:border-border dark:border-slate-700/30",
    iconColor: "text-slate-400",
    iconBg: "bg-slate-100 dark:bg-slate-800",
    btnCls: "bg-slate-400 text-white hover:bg-slate-500",
  },
}

const LICENSE_TYPE_KEYS: Record<string, string> = {
  Concurrent: "licenses.concurrent",
  Host: "licenses.host",
}

export default function LicensesManagement() {
  const t = useTranslation()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<"addons" | "plugins">("addons")
  const [showAddHost, setShowAddHost] = useState(false)
  const [selectedLicense, setSelectedLicense] = useState<ActiveLicense | null>(null)
  const [selectedPlanId, setSelectedPlanId] = useState("1")

  const licenses: ActiveLicense[] = [
    { id: "1", licenseType: "Concurrent", planType: "pro", participants: 300, renewalDate: "Dec 11, 2026", billingCycle: "Monthly", isSelected: true },
    { id: "2", licenseType: "Concurrent", planType: "standard", participants: 500, renewalDate: "Sep 3, 2026", billingCycle: "Yearly" },
    { id: "3", licenseType: "Host", planType: "business", participants: 1000, renewalDate: "Jun 6, 2026", billingCycle: "Yearly", showManage: true },
  ]

  const addons = [
    {
      id: "1", name: "Cloud Storage", description: "30 GB additional storage",
      price: 9, expiryDate: "Dec 15, 2026",
      icon: <Cloud className="w-5 h-5" />,
      gradient: "from-cyan-500 to-blue-500",
      bg: "bg-cyan-50 dark:bg-cyan-900/20",
      color: "text-cyan-600 dark:text-cyan-400",
    },
    {
      id: "2", name: "Webinar Pro", description: "500 attendees capacity",
      price: 84, expiryDate: "Nov 8, 2026",
      icon: <Presentation className="w-5 h-5" />,
      gradient: "from-orange-500 to-rose-500",
      bg: "bg-orange-50 dark:bg-orange-900/20",
      color: "text-orange-600 dark:text-orange-400",
    },
    {
      id: "3", name: "AI Summary", description: "Smart meeting summaries",
      price: 20, expiryDate: "Jan 4, 2027",
      icon: <Sparkles className="w-5 h-5" />,
      gradient: "from-violet-500 to-purple-600",
      bg: "bg-violet-50 dark:bg-violet-900/20",
      color: "text-violet-600 dark:text-violet-400",
    },
  ]

  return (
    <div className="space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-500">

      {/* ─── Section Header ─────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-md font-bold text-slate-900 dark:text-foreground/80 dark:text-white tracking-tight">{t("licenses.activePlans")}</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{licenses.length} {t("licenses.activeLicenses")}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* <button
            onClick={() => navigate("/settings/licenses/cart")}
            className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-foreground border border-border rounded-xl px-4 py-2 transition-all hover:bg-muted hover:border-primary/30"
          >
            <ShoppingCart className="w-4 h-4" />
            <span className="hidden sm:inline">Cart</span>
          </button> */}
          <button
            onClick={() => navigate("/settings/licenses/pricing")}
            className="flex items-center gap-2 text-sm font-semibold text-white bg-primary hover:bg-primary/90 px-4 py-2 rounded-xl transition-all shadow-sm shadow-primary/20 hover:shadow-md hover:shadow-primary/30 active:scale-95"
          >
            <PlusCircle className="w-4 h-4" />
            {t("licenses.buyMore")}
          </button>
        </div>
      </div>

      {/* ─── Active Plan Cards ───────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {licenses.map((license, idx) => {
          const cfg = PLAN_CONFIG[license.planType] ?? PLAN_CONFIG.standard
          const isSelected = selectedPlanId === license.id
          return (
            <motion.div
              key={license.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.1, duration: 0.4 }}
              onClick={() => setSelectedPlanId(license.id)}
              className={`relative group cursor-pointer rounded-2xl border transition-all duration-300 bg-card dark:bg-card overflow-hidden
                ${isSelected
                  ? "border-primary shadow-lg ring-1 ring-primary/20"
                  : "border-slate-200 dark:border-border dark:border-border shadow-sm hover:shadow-md hover:border-slate-300 dark:hover:border-slate-600"
                }
              `}
            >
              {/* Decorative Background Wave */}
              <div className={`absolute inset-0 ${cfg.iconBg}`}>
                <svg 
                  className="absolute bottom-0 right-0 w-full h-full" 
                  viewBox="0 0 200 120" 
                  fill="none" 
                  preserveAspectRatio="none"
                >
                  <path 
                    d="M200 120V80C180 70 160 75 140 80C120 85 100 90 80 85C60 80 40 70 20 75C10 77.5 5 79 0 80V120H200Z" 
                    fill="currentColor" 
                    className="text-white/30 dark:text-white/10"
                  />
                </svg>
              </div>

              <div className="relative z-10 p-4">
                {/* Header with selection radio */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-card/50 dark:bg-card/10 flex items-center justify-center">
                      <div className={cfg.iconColor}>
                        {cfg.icon}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-foreground/80 dark:text-white text-base leading-tight">{t(LICENSE_TYPE_KEYS[license.licenseType] ?? license.licenseType)}</h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                        {cfg.label} • {t(`licenses.${license.billingCycle.toLowerCase()}`)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Selection radio */}
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all duration-200
                    ${isSelected 
                      ? "border-primary bg-primary" 
                      : "border-slate-300 dark:border-slate-600 group-hover:border-primary/50"
                    }`}
                  >
                    {isSelected && <div className="w-2 h-2 bg-card rounded-full" />}
                  </div>
                </div>

                {/* Stats */}
                <div className="space-y-3 mb-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-400">{t("licenses.participants")}</span>
                    <span className="text-sm font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{license.participants}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-400">{t("licenses.renewal")}</span>
                    <span className="text-sm font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{license.renewalDate}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-400">{t("licenses.status")}</span>
                    <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">{t("common.active")}</span>
                  </div>
                </div>

                {/* Action button */}
                <Button
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedLicense(license)
                    setShowAddHost(true)
                  }}
                  variant={'ghost'}
                  className="bg-primary/10 w-full"
                >
                  {t("licenses.manageLicense")}
                </Button>
              </div>
            </motion.div>
          )
        })}
      </div>

      {/* ─── My Subscriptions ───────────────────────────── */}
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-md font-bold text-slate-900 dark:text-foreground/80 dark:text-white tracking-tight">{t("licenses.mySubscriptions")}</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t("licenses.manageAddons")}</p>
          </div>
          <button
            onClick={() => navigate("/settings/licenses/pricing")}
            className="text-sm font-semibold text-primary hover:text-primary/80 flex items-center gap-1 transition-colors"
          >
            {t("licenses.browseAll")} <ChevronRight className="w-3 h-3" />
          </button>
        </div>

        {/* Tab Pills */}
        <div className="flex gap-2 bg-slate-100/80 dark:bg-slate-800/50 p-1 rounded-xl w-fit">
          {(["addons", "plugins"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                activeTab === tab
                  ? "bg-card dark:bg-slate-700 text-slate-900 dark:text-foreground/80 dark:text-white shadow-sm"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              }`}
            >
              {tab === "addons" ? <Package className="w-3.5 h-3.5" /> : <Puzzle className="w-3.5 h-3.5" />}
              {tab === "addons" ? t("licenses.addons") : t("licenses.plugins")}
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === "addons" ? (
          addons.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {addons.map((addon, idx) => (
                <div
                  key={addon.id}
                  className="stagger-card group relative bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-2xl p-5 shadow-xs hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 overflow-hidden"
                  style={{ animationDelay: `${idx * 60}ms` }}
                >
                  {/* Background gradient blob */}
                  <div className={`absolute -top-6 -right-6 w-24 h-24 rounded-full bg-gradient-to-br ${addon.gradient} opacity-5 group-hover:opacity-10 transition-opacity duration-300`} />

                  <div className="flex items-start gap-3 mb-4">
                    <div className={`w-11 h-11 rounded-xl ${addon.bg} flex items-center justify-center ${addon.color} shrink-0 transition-transform duration-200 group-hover:scale-105`}>
                      {addon.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-bold text-slate-900 dark:text-foreground/80 dark:text-white text-sm">{addon.name}</p>
                        <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-0.5 rounded-full shrink-0">{t("common.active").toUpperCase()}</span>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{addon.description}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-stroke-100 dark:border-border dark:border-border">
                    <div className="flex items-center gap-1.5">
                      <RefreshCw className="w-3 h-3 text-slate-400" />
                      <span className="text-xs text-slate-500 dark:text-slate-400">{t("licenses.expires")} {addon.expiryDate}</span>
                    </div>
                    <span className="text-sm font-bold text-slate-900 dark:text-foreground/80 dark:text-white">${addon.price}<span className="text-xs font-normal text-slate-500">{t("licenses.mo")}</span></span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 bg-gradient-to-b from-slate-50 to-white dark:from-slate-800/30 dark:to-card rounded-2xl border border-dashed border-slate-200 dark:border-border dark:border-border">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                <Package className="w-8 h-8 text-primary" />
              </div>
              <h4 className="text-base font-bold text-slate-700 dark:text-white mb-1.5">{t("licenses.noAddons")}</h4>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 text-center max-w-xs">{t("licenses.noAddonsDesc")}</p>
              <Button onClick={() => navigate("/settings/licenses/pricing")} className="bg-primary text-white hover:bg-primary/90 px-8 shadow-sm shadow-primary/20">
                {t("licenses.browseAddons")}
              </Button>
            </div>
          )
        ) : (
          <div className="flex flex-col items-center justify-center py-20 bg-gradient-to-b from-slate-50 to-white dark:from-slate-800/30 dark:to-card rounded-2xl border border-dashed border-slate-200 dark:border-border dark:border-border">
            <div className="w-32 h-32 flex items-center justify-center">
              <img src="/images/nodataimagenew.svg" alt="" className="w-full h-full"/>
            </div>
            <h4 className="text-base font-bold text-slate-700 dark:text-white mb-1.5">{t("licenses.noPlugins")}</h4>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 text-center max-w-xs">{t("licenses.noPluginsDesc")}</p>
            <Button onClick={() => navigate("/settings/licenses/pricing")} className="bg-primary text-white hover:bg-primary/90 px-8">
              {t("licenses.explorePlugins")}
            </Button>
          </div>
        )}
      </div>

      <AddHostDialog open={showAddHost} onOpenChange={setShowAddHost} license={selectedLicense as any} />
    </div>
  )
}
